import React, { useState, useEffect } from 'react';

// ── Step definitions ──────────────────────────────────────────────────────

export const USER_TOUR_STEPS = [
  {
    targetId: null, icon: '👋', title: 'Welcome to SolutionOne ERP!',
    text: 'This quick tour walks you through the key features. Ask any business question in plain English — no SQL knowledge needed.'
  },
  {
    targetId: 'tour-input', icon: '✍️', title: 'Ask in plain English',
    text: 'Type your question here. Example: "Show top 10 customers by revenue last month". Hit Enter to send.'
  },
  {
    targetId: 'tour-suggestions', icon: '💡', title: 'Quick-start suggestions',
    text: 'Click any chip to instantly run a popular query. Great for exploring what you can ask.'
  },
  {
    targetId: 'tour-send-btn', icon: '🚀', title: 'Run your query',
    text: "You'll get the generated SQL, a results table, and an AI summary — all in one response."
  },
  {
    targetId: 'tour-dashboard-btn', icon: '📊', title: 'Analytics Dashboard',
    text: 'After any query, click Dashboard to view charts, KPIs, and trends for that data with one click.'
  },
  {
    targetId: null, icon: '📥', title: 'Export & Save',
    text: 'Every result has PDF and Excel export buttons. Use Save to bookmark a query for quick access later.'
  },
  {
    targetId: null, icon: '🔁', title: 'Follow-up questions',
    text: 'Ask naturally: "show only won ones", "filter by last month", "top 5 of those" — the assistant remembers context.'
  },
  {
    targetId: null, icon: '🎉', title: "You're all set!",
    text: 'Start exploring your ERP data. Click any suggestion or type your own question to begin.'
  },
];

export const ADMIN_EXTRA_STEPS = [
  {
    targetId: null, icon: '🛡️', title: 'Admin Panel access',
    text: 'Click "Admin Panel" in the sidebar to manage the full system — only visible to admins.'
  },
  {
    targetId: null, icon: '⚙️', title: 'Model & Config',
    text: 'Set the AI model, temperature, max rows, and API key from the Model Config tab.'
  },
  {
    targetId: null, icon: '👥', title: 'User Management',
    text: 'Create users, assign roles (admin/user), activate/deactivate accounts, and reset passwords.'
  },
  {
    targetId: null, icon: '🔒', title: 'Security & Schema',
    text: 'Block sensitive keywords, set daily rate limits per user, and manage table definitions and column aliases.'
  },
];

// ── Component ─────────────────────────────────────────────────────────────

export default function OnboardingTour({ steps, onComplete }) {
  const [step, setStep]   = useState(0);
  const [rect, setRect]   = useState(null);
  const [ready, setReady] = useState(false);

  const current = steps[step];
  const isLast  = step === steps.length - 1;
  const PAD     = 10;
  const TW      = 310;

  // Resolve target element rect on each step change
  useEffect(() => {
    setReady(false);
    const t = setTimeout(() => {
      if (current.targetId) {
        const el = document.getElementById(current.targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          setTimeout(() => {
            setRect(el.getBoundingClientRect());
            setReady(true);
          }, 220);
          return;
        }
      }
      setRect(null);
      setReady(true);
    }, 60);
    return () => clearTimeout(t);
  }, [step, current.targetId]);

  // Recalculate on resize
  useEffect(() => {
    if (!current.targetId) return;
    const onResize = () => {
      const el = document.getElementById(current.targetId);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [step, current.targetId]);

  const next = () => isLast ? onComplete() : setStep(s => s + 1);

  // Tooltip position
  let tipStyle;
  if (!rect) {
    tipStyle = { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: TW };
  } else {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.min(Math.max(rect.left - PAD, 12), vw - TW - 12);
    tipStyle = (rect.bottom + 180 < vh)
      ? { position: 'fixed', top: rect.bottom + PAD + 8, left, width: TW }
      : { position: 'fixed', top: Math.max(rect.top - 180, 12), left, width: TW };
  }

  if (!ready) return null;

  return (
    <>
      {/* Click-blocker backdrop */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 9000 }} />

      {/* Spotlight or plain overlay */}
      {rect ? (
        <div style={{
          position: 'fixed',
          top: rect.top - PAD, left: rect.left - PAD,
          width: rect.width + PAD * 2, height: rect.height + PAD * 2,
          borderRadius: 10,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.58)',
          border: '2px solid #1B5E3B',
          zIndex: 9001, pointerEvents: 'none',
          transition: 'all 0.3s ease'
        }} />
      ) : (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.58)', zIndex: 9001, pointerEvents: 'none' }} />
      )}

      {/* Tooltip card */}
      <div
        style={{ ...tipStyle, zIndex: 9002, animation: 'slideUpCard 0.25s ease both' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={S.card}>
          {/* Header row */}
          <div style={S.cardHeader}>
            <span style={S.badge}>{step + 1} / {steps.length}</span>
            <button style={S.skipBtn} onClick={onComplete}>Skip tour</button>
          </div>

          {/* Title */}
          <div style={S.titleRow}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>{current.icon}</span>
            <span style={S.title}>{current.title}</span>
          </div>

          {/* Body */}
          <p style={S.body}>{current.text}</p>

          {/* Footer: dots + button */}
          <div style={S.footer}>
            <div style={S.dots}>
              {steps.map((_, i) => (
                <div key={i} style={{
                  ...S.dot,
                  width: i === step ? 18 : 6,
                  background: i === step ? '#1B5E3B' : '#e8e6e0'
                }} />
              ))}
            </div>
            <button style={S.nextBtn} onClick={next}>
              {isLast ? 'Done 🎉' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────

const S = {
  card: {
    background: '#fff', borderRadius: 14, padding: '18px 18px 14px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.22), 0 4px 16px rgba(27,94,59,0.12)',
    border: '1px solid rgba(27,94,59,0.18)'
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  badge: {
    background: 'linear-gradient(135deg, #1B5E3B, #2E7D52)',
    color: '#fff', fontSize: 10, fontWeight: 700,
    padding: '3px 10px', borderRadius: 20, letterSpacing: '0.4px'
  },
  skipBtn: {
    background: 'none', border: '1px solid #d0d0d0', color: '#666',
    fontSize: 11, fontWeight: 500, cursor: 'pointer',
    padding: '4px 10px', borderRadius: 6,
  },
  titleRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  title: { fontSize: 14, fontWeight: 700, color: '#1a1a1a' },
  body: { fontSize: 13, color: '#4b4b4b', lineHeight: 1.65, margin: '0 0 14px' },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  dots: { display: 'flex', gap: 4, alignItems: 'center' },
  dot: { height: 6, borderRadius: 3, transition: 'all 0.25s ease' },
  nextBtn: {
    background: 'linear-gradient(135deg, #1B5E3B, #2E7D52)',
    color: '#fff', border: 'none', borderRadius: 8,
    padding: '8px 18px', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', boxShadow: '0 4px 12px rgba(27,94,59,0.35)'
  }
};
